// Flows preview mode (`?preview=flows`).
//
// The internal admin consoles embed these hosted pages live in iframes on a
// "Flows" canvas, several at once and reloaded often. In this mode a page must
// render exactly as it would for a customer in the same state while causing no
// production side effects (no analytics, no experiment exposure, no payment,
// no email, no extension messaging). It also reports its natural size and the
// customer actions it would have taken to the framing canvas.
//
// Deliberately independent from `?preview=dashboard` (the dashboard's catalog
// preview, which receives its catalog over postMessage).
//
// CommonJS so node:test can load it directly, like the other pure modules.

// Keep in sync with the inline Google tag gate in each page's index.html,
// which must decide before any bundle loads.
const FLOWS_PREVIEW_PATTERN = /[?&]preview=flows(&|$)/

function currentSearch () {
  return (typeof window !== 'undefined' && window.location && window.location.search) || ''
}

function isFlowsPreview (search = currentSearch()) {
  return FLOWS_PREVIEW_PATTERN.test(search || '')
}

// State overrides (e.g. `previewState=owned`) are only honored in flows
// preview so a customer-facing URL can never be forced into a state.
function flowsPreviewState (search = currentSearch(), allowed = null) {
  if (!isFlowsPreview(search)) return null
  const value = new URLSearchParams(search).get('previewState')
  if (!value) return null
  const state = value.trim().toLowerCase()
  if (Array.isArray(allowed) && !allowed.includes(state)) return null
  return state || null
}

function isFramed (win) {
  try {
    return Boolean(win && win.parent && win.parent !== win)
  } catch (_) {
    return true
  }
}

// Posts to the framing canvas. Only in flows preview and only when framed —
// a top-level page never broadcasts anything.
function postToFlows (message, { win = typeof window !== 'undefined' ? window : undefined } = {}) {
  if (!win || !isFlowsPreview((win.location && win.location.search) || '')) return false
  if (!isFramed(win)) return false
  try {
    win.parent.postMessage(message, '*')
    return true
  } catch (_) {
    return false
  }
}

function flowsEventMessage (name, detail) {
  const message = { type: 'flows:event', name }
  if (detail !== undefined) message.detail = detail
  return message
}

function postFlowsEvent (name, detail, options) {
  return postToFlows(flowsEventMessage(name, detail), options)
}

// Natural size of a page: the container's horizontal padding around its widest
// child at that child's design width (its max-width), and the distance from the
// container's top edge to the lowest measured bottom edge (the container plus
// anything after it, like the site footer). Neither depends on the iframe's
// height, so body { min-height: 100vh } can't feed back into it.
function computeFlowsSize ({ top = 0, paddingLeft = 0, paddingRight = 0, childWidths = [], bottoms = [] }) {
  const widths = childWidths.filter((value) => Number.isFinite(value) && value > 0)
  const inner = widths.length ? Math.max(...widths) : 0
  const lowest = bottoms.filter(Number.isFinite)
  return {
    width: Math.ceil(paddingLeft + inner + paddingRight),
    height: Math.ceil(Math.max(top, ...lowest) - top)
  }
}

function designWidth (child, win) {
  const rectWidth = child.getBoundingClientRect().width
  const maxWidth = parseFloat(win.getComputedStyle(child).maxWidth)
  return Number.isFinite(maxWidth) && maxWidth > 0 ? maxWidth : rectWidth
}

function measureFlowsSize (el, include = [], win = window) {
  const rect = el.getBoundingClientRect()
  // Hidden (not yet revealed) containers have no box — nothing to report.
  if (rect.width === 0 && rect.height === 0) return null
  const style = win.getComputedStyle(el)
  const bottoms = [el, ...include]
    .filter(Boolean)
    .map((node) => {
      const box = node.getBoundingClientRect()
      if (box.width === 0 && box.height === 0) return NaN
      return box.bottom + (parseFloat(win.getComputedStyle(node).marginBottom) || 0)
    })
  return computeFlowsSize({
    top: rect.top,
    paddingLeft: parseFloat(style.paddingLeft) || 0,
    paddingRight: parseFloat(style.paddingRight) || 0,
    childWidths: Array.from(el.children).map((child) => designWidth(child, win)),
    bottoms
  })
}

// Reports `{ type: 'flows:size', width, height }` whenever the page's natural
// size changes. `el` is the page's main container; `include` lists siblings
// rendered after it (the footer) that belong to the frame's height.
function observeFlowsSize (el, { include = [], win = typeof window !== 'undefined' ? window : undefined } = {}) {
  if (!el || !win || !isFlowsPreview(win.location.search) || !isFramed(win)) return () => {}

  let last = null
  const report = () => {
    const size = measureFlowsSize(el, include, win)
    if (!size) return
    if (last && last.width === size.width && last.height === size.height) return
    last = size
    postToFlows({ type: 'flows:size', width: size.width, height: size.height }, { win })
  }

  const targets = [el, ...include].filter(Boolean)
  let observer = null
  if (typeof win.ResizeObserver === 'function') {
    observer = new win.ResizeObserver(report)
    targets.forEach((target) => observer.observe(target))
  }
  win.addEventListener('load', report)
  report()

  return () => {
    if (observer) observer.disconnect()
    win.removeEventListener('load', report)
  }
}

module.exports = {
  FLOWS_PREVIEW_PATTERN,
  isFlowsPreview,
  flowsPreviewState,
  postToFlows,
  postFlowsEvent,
  flowsEventMessage,
  computeFlowsSize,
  measureFlowsSize,
  observeFlowsSize
}
